'use client';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import useFormattedDate from '@/hooks/useFormattedDate';
import useMixPanel from '@/hooks/useMixPanel';
import {
  getHoursLeft,
  hasPendingActions,
  PaymentStatus
} from '@/utils/reservations';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import AlertSection from '../../alert/AlertSection';
import { SaleCardProps } from './saleCard.types';

const SaleCard: React.FC<SaleCardProps> = ({ service }) => {
  const { t } = useTranslation(['bookings', 'common']);
  const { formattedDate } = useFormattedDate();
  const router = useRouter();
  const MixPanel = useMixPanel();

  const serviceInfo = service.service;
  const participants = service.participants;
  const reservations = service.reservations;
  const statusCanceled = service.status.name === 'canceled';

  const isSomePaymentApproved = service.payments.some(
    (payment) => payment.paymentStatus.name === PaymentStatus.Approved
  );
  const isAllInsuranceDataFilled = participants.every(
    (participant) => participant.hasFilledData
  );
  const hasPendingReservations = hasPendingActions(reservations);

  return (
    <Card
      className={`flex flex-col justify-between relative ${
        statusCanceled ? 'pointer-events-none' : 'cursor-pointer'
      } `}
      onClick={() => {
        MixPanel.track('press manage reservation', {
          screen: 'bookings',
          serviceName: serviceInfo.name
        });
        router.push(`/bookings/${service.id}`);
      }}
    >
      <div>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">
            <p className="text-primary">{service.organization.name}</p>

            <p>{serviceInfo.name}</p>
            <p className="text-xs mt-2 text-gray-400">
              {formattedDate({ date: service.createdAt })}
            </p>
          </CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col">
          <li className="text-sm text-muted-foreground  my-1 ">
            {participants?.length +
              ' ' +
              t('bookingDetailSection.participants')}
          </li>
          <li className="text-sm text-muted-foreground  my-1 ">
            {t('bookingDetailSection.hoursLeft') +
              ': ' +
              getHoursLeft(reservations)}
          </li>
        </CardContent>
      </div>

      <CardFooter className="gap-2 flex flex-col">
        {statusCanceled ? (
          <AlertSection
            variant="error"
            text={t('alertServicesCanceled', {
              observation: service.status.observation ?? ''
            })}
          />
        ) : (
          <>
            {!isSomePaymentApproved && (
              <AlertSection
                variant="error"
                text={t('bookingDetailSection.missingPaymentAlert')}
              />
            )}
            {!isAllInsuranceDataFilled && (
              <AlertSection variant="error" text={t('insuranceWarning')} />
            )}
            {hasPendingReservations && (
              <AlertSection variant="warning" text={t('pendingReservations')} />
            )}
          </>
        )}

        {!statusCanceled && (
          <Button className="w-full">{t('ManageReservation')}</Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default SaleCard;
